<template>
  <div class="tracking-app tracking-app-zh" lang="zh-CN">
    <header class="tracking-nav">
      <div class="tracking-brand"><b>Phoenix</b></div>
      <nav>
        <router-link class="active" to="/">货物追踪</router-link>
        <router-link to="/quota">申请报价</router-link>
      </nav>
      <div class="nav-right-slot">
        <button class="language-switch" type="button" @click="$emit('switch-language')" aria-label="Switch to English page">EN</button>
      </div>
    </header>

    <section class="tracking-hero">
      <div class="hero-content">
        <small class="hero-brand">PHOENIX 供应链</small>
        <h1>随时掌握货物动态</h1>
        <p class="hero-sub">输入追踪单号，即可查询海运、空运及 Air&&EXpress 货物的最新状态。</p>
        <div class="tracking-search">
          <input
            v-model.trim="query"
            @keyup.enter="search"
            placeholder="请输入系统单号，例如 SEAE000000000 / AIRE000000000"
          >
          <button :disabled="loading" @click="search">
            <span v-if="loading" class="track-spinner"></span>{{ loading ? '查询中...' : '查询货物' }}
          </button>
        </div>
        <div v-if="loading" class="query-loading">正在查询货物信息...</div>
        <div v-if="error" class="query-error">{{ error }}</div>
      </div>
    </section>

    <main v-if="shipment" class="tracking-main">
      <!-- 模板 A: Shipments 传统海空运模板（当查的是 Shipments 界面添加的数据时渲染：100% 还原经典里程碑模板） -->
      <section v-if="!shipment.isExpress && shipment.source !== 'EXPRESS'" class="shipment-card">
        <div class="shipment-head">
          <div class="shipment-title">
            <div class="transport-icon">{{ shipment.transportMode === 'SEA' ? '⚓' : '✈' }}</div>
            <div>
              <h2>{{ shipment.trackingNo }}</h2>
              <p class="route-text">{{ shipment.origin }} → {{ shipment.destination }}</p>
            </div>
          </div>
          <span class="mode-pill">{{ shipment.transportMode === 'SEA' ? '海运' : '空运' }}</span>
        </div>

        <section v-if="hasShipmentDetails" class="cargo-summary">
          <h3>货物信息</h3>
          <div class="cargo-grid">
            <div v-if="hasValue(shipment.cargoName)" class="cargo-field cargo-name full-row">
              <span class="field-label">货物名称</span><b class="field-value">{{ shipment.cargoName }}</b>
              <button type="button" class="view-cargo" @click="showCargoModal = true">查看全部</button>
            </div>
            <div v-if="hasValue(shipment.supplierName)" class="cargo-field full-row">
              <span class="field-label">供应商</span><b class="field-value">{{ shipment.supplierName }}</b>
            </div>
            <div v-if="hasValue(shipment.weight)" class="cargo-field">
              <span class="field-label">重量</span><b class="field-value">{{ shipment.weight }}</b>
            </div>
            <div v-if="hasValue(shipment.volume)" class="cargo-field">
              <span class="field-label">体积</span><b class="field-value">{{ shipment.volume }} CBM</b>
            </div>
            <div v-if="hasValue(shipment.packageQuantity)" class="cargo-field">
              <span class="field-label">件数</span><b class="field-value">{{ packageCount(shipment.packageQuantity) }}</b>
            </div>
          </div>
        </section>

        <div class="timeline">
          <article v-for="event in shipment.events" :key="event.nodeCode" class="timeline-event">
            <span class="timeline-dot" :class="{ done: event.statusCode }"></span>
            <div class="timeline-content">
              <div class="event-title">
                <b>{{ eventName(event) }}</b>
                <span v-if="event.statusCode && event.statusLabel && event.statusLabel !== '待维护状态'" class="node-status-tag">{{ eventStatus(event) }}</span>
              </div>
              <small v-if="event.occurredAt" class="event-time">更新时间：{{ formatDate(event.occurredAt) }}</small>
              <div v-if="event.fields && event.fields.length" class="event-fields">
                <div v-for="field in event.fields" v-if="showField(event, field)" :key="field.label">
                  <span>{{ field.label }}</span><b>{{ field.value }}</b>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- 模板 B: Air&&EXpress 专属模板（货物信息按 Shipments 来，字号大，时间醒目） -->
      <section v-else class="shipment-card express-card">
        <div class="shipment-head">
          <div class="shipment-title">
            <div class="transport-icon">✈</div>
            <div>
              <h2 class="shipment-no-title">{{ shipment.trackingNo || shipment.systemNo }}</h2>
              <p class="route-text">{{ shipment.origin || 'CAN' }} → {{ shipment.destination || 'SYD' }}</p>
            </div>
          </div>
          <span class="mode-pill">{{ carrierName(shipment.carrier) }}</span>
        </div>

        <!-- 货物信息：完全按照 Shipments 的格式和排列（不含 track number） -->
        <section v-if="hasShipmentDetails" class="cargo-summary">
          <h3>货物信息</h3>
          <div class="cargo-grid">
            <div v-if="hasValue(shipment.cargoName)" class="cargo-field cargo-name full-row">
              <span class="field-label">货物名称</span><b class="field-value">{{ shipment.cargoName }}</b>
              <button type="button" class="view-cargo" @click="showCargoModal = true">查看全部</button>
            </div>
            <div v-if="hasValue(shipment.supplierName)" class="cargo-field full-row">
              <span class="field-label">供应商</span><b class="field-value">{{ shipment.supplierName }}</b>
            </div>
            <div v-if="hasValue(shipment.weight)" class="cargo-field">
              <span class="field-label">重量</span><b class="field-value">{{ shipment.weight }}</b>
            </div>
            <div v-if="hasValue(shipment.volume)" class="cargo-field">
              <span class="field-label">体积</span><b class="field-value">{{ shipment.volume }} CBM</b>
            </div>
            <div v-if="hasValue(shipment.packageQuantity)" class="cargo-field">
              <span class="field-label">件数</span><b class="field-value">{{ packageCount(shipment.packageQuantity) }}</b>
            </div>
          </div>
        </section>

        <!-- 5大节点横向排列（选了的节点就展示，不选不展示，时间大号加粗） -->
        <section v-if="selectedMilestoneEvents.length" class="milestone-stepper-wrap">
          <div class="horizontal-stepper">
            <div
              v-for="(ev, idx) in selectedMilestoneEvents"
              :key="ev.nodeCode || idx"
              class="stepper-item"
              :class="{
                'step-done': ev.statusCode === 'COMPLETED',
                'step-active': ev.statusCode === 'PROCESSING',
                'step-wait': !ev.statusCode || ev.statusCode === 'PENDING'
              }"
            >
              <div class="line-left" v-if="idx > 0"></div>
              <div class="step-circle">
                <span v-if="ev.statusCode === 'COMPLETED'">✓</span>
                <span v-else-if="ev.statusCode === 'PROCESSING'">●</span>
                <span v-else>{{ idx + 1 }}</span>
              </div>
              <div class="line-right" v-if="idx < selectedMilestoneEvents.length - 1"></div>
              <div class="step-label-box">
                <div class="step-name">{{ expressNodeNameZh(ev.nodeCode, ev.name) }}</div>
                <div class="step-status">{{ expressNodeStatusZh(ev.statusCode) }}</div>
                <div v-if="ev.occurredAt" class="step-time">{{ formatDate(ev.occurredAt) }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 最下方：只放抓取的实时轨迹内容（时间字号加大至15px，清晰加深） -->
        <div class="timeline-section">
          <div class="section-head">
            <h3>实时轨迹明细 (Real-time Tracking Trajectory)</h3>
            <small v-if="shipment.crawlerEvents && shipment.crawlerEvents.length">
              共 {{ shipment.crawlerEvents.length }} 条记录
            </small>
          </div>

          <!-- 抓取轨迹流 -->
          <div v-if="shipment.crawlerEvents && shipment.crawlerEvents.length" class="timeline">
            <article
              v-for="(ce, idx) in shipment.crawlerEvents"
              :key="idx"
              class="timeline-event"
              :class="{ 'latest-event': idx === 0 }"
            >
              <span class="timeline-dot" :class="{ done: idx === 0 }"></span>
              <div class="timeline-content">
                <div class="event-title">
                  <b>{{ ce.status || ce.description || '状态更新' }}</b>
                  <span v-if="ce.location" class="location-badge">{{ ce.location }}</span>
                  <span v-if="idx === 0" class="tag-latest">最新进展</span>
                </div>
                <div class="event-time">发生时间：{{ ce.time }}</div>
                <p v-if="ce.description && ce.description !== ce.status" class="event-desc">{{ ce.description }}</p>
              </div>
            </article>
          </div>

          <div v-else class="empty-trajectory-box">
            <p>暂无抓取的实时轨迹记录，请等待承运商系统更新或稍后点击查询。</p>
          </div>
        </div>
      </section>
    </main>

    <section v-else class="query-placeholder">
      <div>⌕</div>
      <p>输入追踪单号，查看货物运输进度</p>
    </section>

    <!-- 完整货物名称弹窗 -->
    <div v-if="showCargoModal" class="cargo-modal-backdrop" @click.self="showCargoModal = false">
      <section class="cargo-modal" role="dialog" aria-modal="true">
        <header>
          <h2>完整货物名称</h2>
          <button type="button" aria-label="关闭" @click="showCargoModal = false">×</button>
        </header>
        <p>{{ shipment.cargoName }}</p>
      </section>
    </div>

    <footer>© 2026 Phoenix Logistics · 每一票货物都清晰可见</footer>
  </div>
</template>

<script>
import mixin from './tracking-page-mixin'

const nodeNames = {
  ORDER_ACCEPTED: '接受订单',
  BOOKING: '订舱',
  RELEASE: '放舱',
  CONTAINER_LOADING: '装柜',
  CUSTOMS_DECLARATION: '报关',
  DEPARTURE: '开船',
  PRE_CLEARANCE: '预清关',
  ARRIVAL_PORT: '到港',
  CONTAINER_UNLOADING: '拆柜',
  DELIVERY: '派送',
  EMPTY_RETURN: '还空柜',
  WAREHOUSE_IN: '入仓',
  TAKE_OFF: '起飞',
  PICKUP_DELIVERY: '提货派送',
  SHIPMENT: '发货',
  CUSTOMS: '报关',
  CARRIER_TRACKING: '官网轨迹',
  CLEARANCE: '清关'
}

const statusNames = {
  ACCEPTED: '已接单',
  RECEIVED: '已收到订单',
  BOOKED: '已订舱',
  NOT_BOOKED: '未订舱',
  RELEASED: '已放舱',
  NOT_RELEASED: '未放舱',
  LOADED: '已装柜',
  NOT_LOADED: '未装柜',
  DECLARED: '已报关',
  NOT_DECLARED: '未报关',
  DECLARATION_SUCCESS: '报关成功',
  DECLARATION_PROCESSING: '报关中',
  UNDER_DECLARATION: '报关中',
  DECLARATION_FAILED: '报关异常',
  DEPARTED: '已开船',
  NOT_DEPARTED: '未开船',
  IN_TRANSIT: '运输中',
  CLEARED: '已清关',
  CLEARING: '清关中',
  NOT_CLEARED: '未清关',
  ARRIVED: '已到港',
  NOT_ARRIVED: '未到港',
  UNPACKED: '已拆柜',
  UNPACKING: '拆柜中',
  NOT_UNPACKED: '未拆柜',
  DELIVERED: '已派送',
  DELIVERING: '派送中',
  WAITING_DELIVERY_YES: '等待派送中：是',
  WAITING_DELIVERY_NO: '等待派送中：否',
  RETURNED: '已还空柜',
  NOT_RETURNED: '未还空柜',
  AIR_RECEIVED: '已收到订单',
  AIR_NOT_RECEIVED: '未收到货物',
  AIR_BOOKED: '已订舱',
  AIR_NOT_BOOKED: '未订舱',
  AIR_RELEASED: '已放舱',
  AIR_NOT_RELEASED: '未放舱',
  WAREHOUSE_IN: '已入仓',
  NOT_WAREHOUSE_IN: '未入仓',
  TAKEN_OFF: '已起飞',
  NOT_TAKEN_OFF: '未起飞',
  AIR_CLEARED: '已清关',
  AIR_CLEARING: '清关中',
  AIR_NOT_CLEARED: '未清关',
  AIR_ARRIVED: '已到港',
  AIR_NOT_ARRIVED: '未到港',
  PICKED_UP: '已提货',
  NOT_PICKED_UP: '未提货',
  COMPLETED: '已完成',
  PROCESSING: '进行中',
  PENDING: '待更新'
}

export default {
  name: 'TrackingChinesePage',
  mixins: [mixin],
  computed: {
    emptyTrackingMessage: () => '请输入追踪单号。',
    longTrackingMessage: () => '追踪单号过长，请检查后重试。',
    notFoundMessage: () => '未找到该票货物，请检查追踪单号后重试。',
    unavailableMessage: () => '查询服务暂时不可用，请稍后重试。',
    selectedMilestoneEvents () {
      if (!this.shipment || !this.shipment.events) return []
      return this.shipment.events.filter(e => e.isEnabled !== 'N' && e.isEnabled !== false)
    }
  },
  methods: {
    eventName: e => nodeNames[e.nodeCode] || e.name,
    eventStatus: e => (e.statusCode && statusNames[e.statusCode]) || (e.statusLabel && e.statusLabel !== '待维护状态' ? e.statusLabel : ''),
    expressNodeNameZh (code, defaultName) {
      const map = {
        SHIPMENT: '发货',
        CUSTOMS: '报关',
        CARRIER_TRACKING: '官网轨迹',
        CLEARANCE: '清关',
        DELIVERY: '派送'
      }
      return map[code] || defaultName || code
    },
    expressNodeStatusZh (statusCode) {
      if (statusCode === 'COMPLETED') return '已完成'
      if (statusCode === 'PROCESSING') return '进行中'
      return '待更新'
    },
    formatDate (value) {
      if (!value) return ''
      const m = String(value).match(/^(\d{4})[-/](\d{2})[-/](\d{2})(?:[ T](\d{2}:\d{2}))?/)
      return m ? `${m[1]}年${Number(m[2])}月${Number(m[3])}日${m[4] ? ` ${m[4]}` : ''}` : value
    },
    carrierName (c) {
      return {
        CHINA_SOUTHERN: '南方航空 (CSAir)',
        DHL: 'DHL 国际快递',
        QIANQIU: '钱球 (BaslExpress)'
      }[c] || c
    }
  }
}
</script>

<style scoped>
.hero-brand {
  font-size: 14px;
  font-weight: 800;
  color: #1e40af;
  letter-spacing: 1.5px;
}
.hero-sub {
  font-size: 16px;
  color: #1e293b;
  font-weight: 600;
}
.shipment-head {
  padding: 24px 32px;
}
.shipment-no-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}
.route-text {
  font-size: 16px;
  color: #334155;
  font-weight: 700;
  margin-top: 6px;
}
.mode-pill {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1.5px solid #bfdbfe;
  font-size: 15px;
  font-weight: 800;
  padding: 8px 18px;
  border-radius: 20px;
}
.cargo-summary {
  padding: 24px 32px;
}
.cargo-summary h3 {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 16px;
}
.cargo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.cargo-field {
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field-label {
  font-size: 14px;
  color: #475569;
  font-weight: 700;
}
.field-value {
  font-size: 17px;
  color: #0f172a;
  font-weight: 800;
}
.view-cargo {
  font-size: 14px;
  font-weight: 700;
  color: #2563eb;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 4px;
  border: none;
  background: none;
  padding: 0;
}
.milestone-stepper-wrap {
  padding: 28px 32px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}
.horizontal-stepper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  width: 100%;
}
.stepper-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  text-align: center;
}
.step-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  z-index: 2;
  border: 2px solid #cbd5e1;
  transition: all 0.3s;
}
.step-done .step-circle {
  background: #10b981;
  color: #fff;
  border-color: #059669;
}
.step-active .step-circle {
  background: #2563eb;
  color: #fff;
  border-color: #1d4ed8;
  box-shadow: 0 0 0 5px #bfdbfe;
}
.line-left, .line-right {
  position: absolute;
  top: 19px;
  height: 3px;
  background: #cbd5e1;
  z-index: 1;
}
.line-left {
  left: 0;
  right: 50%;
}
.line-right {
  left: 50%;
  right: 0;
}
.step-done .line-left, .step-done .line-right {
  background: #10b981;
}
.step-label-box {
  margin-top: 12px;
}
.step-name {
  font-size: 16px;
  color: #0f172a;
  font-weight: 800;
  display: block;
}
.step-status {
  font-size: 14px;
  color: #475569;
  font-weight: 700;
  margin-top: 4px;
  display: block;
}
.step-done .step-status {
  color: #059669;
  font-weight: 800;
}
.step-active .step-status {
  color: #2563eb;
  font-weight: 800;
}
.step-time {
  font-size: 14px;
  color: #1e293b;
  font-weight: 700;
  margin-top: 5px;
  display: block;
}
.timeline-section {
  padding: 28px 32px;
}
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 22px;
}
.section-head h3 {
  margin: 0;
  font-size: 18px;
  color: #0f172a;
  font-weight: 800;
}
.section-head small {
  color: #475569;
  font-size: 14px;
  font-weight: 700;
}
.event-title b {
  font-size: 17px;
  color: #0f172a;
  font-weight: 800;
}
.latest-event .timeline-dot {
  width: 16px;
  height: 16px;
  border-color: #10b981;
  background: #10b981;
  box-shadow: 0 0 0 5px #a7f3d0;
}
.location-badge {
  background: #dbeafe;
  color: #1e40af;
  font-size: 13px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 5px;
  margin-left: 10px;
}
.tag-latest {
  background: #dcfce7;
  color: #15803d;
  font-size: 13px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 5px;
  border: 1px solid #86efac;
  margin-left: 8px;
}
.event-time {
  color: #1e293b;
  font-size: 15px;
  font-weight: 700;
  margin-top: 6px;
  display: block;
}
.event-desc {
  margin: 8px 0 0;
  color: #1e293b;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.7;
}
.empty-trajectory-box {
  padding: 32px;
  text-align: center;
  color: #475569;
  font-size: 15px;
  font-weight: 600;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}
</style>
